import React from "react";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Sparkles,
    Activity,
    BrainCircuit,
    Code2,
    Play,
} from "lucide-react";
import { fadeUp } from "@/lib/motion";
import Starfield from "@/components/landing/effects/Starfield";
import Aurora from "@/components/landing/effects/Aurora";
import RotatingRing from "@/components/landing/effects/RotatingRing";

const tickerStats = [
    { label: "WPM", value: "120+", icon: Activity },
    { label: "Govt Exams", value: "35+", icon: BrainCircuit },
    { label: "Projects", value: "40+", icon: Code2 },
];

const headline = ["Engineering", "the", "future", "of", "Indian", "software."];

const word = {
    hidden: { y: "120%", opacity: 0 },
    show: (i) => ({
        y: "0%",
        opacity: 1,
        transition: {
            delay: 0.25 + i * 0.08,
            duration: 0.9,
            ease: [0.22, 1, 0.36, 1],
        },
    }),
};

const Hero = () => {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="section relative min-h-[100svh] flex items-center pt-28 pb-16"
        >
            {/* layered background */}
            <div className="absolute inset-0 bg-grid pointer-events-none" />
            <Aurora variant="cyan" />
            <Starfield />
            <RotatingRing />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505] pointer-events-none" />

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full">
                <div className="flex flex-col items-center text-center">
                    {/* badge */}
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        data-testid="hero-badge"
                        className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-mono uppercase tracking-[0.25em] text-cyan-300"
                    >
                        <span className="relative inline-flex h-2 w-2">
                            <span className="absolute inset-0 rounded-full bg-cyan-400 pulse-ring" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-300" />
                        </span>
                        Innovating from Prayagraj · Est. 2022
                    </motion.span>

                    {/* heading with per-word reveal */}
                    <h1
                        data-testid="hero-heading"
                        className="mt-8 font-display font-bold text-white text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.95] tracking-tight max-w-5xl"
                    >
                        <span className="flex flex-wrap justify-center gap-x-4 gap-y-2">
                            {headline.map((w, i) => (
                                <span key={i} className="overflow-hidden inline-flex">
                                    <motion.span
                                        custom={i}
                                        variants={word}
                                        initial="hidden"
                                        animate="show"
                                        className={
                                            i === 2 || i === 3
                                                ? "gradient-text"
                                                : "text-white"
                                        }
                                    >
                                        {w}
                                    </motion.span>
                                </span>
                            ))}
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.1 }}
                        data-testid="hero-subheading"
                        className="mt-6 max-w-2xl text-lg md:text-xl text-zinc-400 leading-relaxed font-body"
                    >
                        <span className="text-white font-semibold">
                            Atvicon Technologies
                        </span>{" "}
                        builds AI-first products for Indian aspirants and advocates — and
                        ships production-grade software, mobile apps and marketing
                        automation for ambitious teams.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 1.3 }}
                        className="mt-10 flex flex-col sm:flex-row items-center gap-4"
                    >
                        <a
                            href="#products"
                            data-testid="hero-cta-primary"
                            className="btn-shine relative inline-flex items-center gap-2 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-black font-semibold text-base px-7 py-4 transition shadow-[0_0_40px_-10px_rgba(0,229,255,0.6)]"
                        >
                            <Sparkles className="h-4 w-4" />
                            Explore Products
                        </a>
                        <a
                            href="#contact"
                            data-testid="hero-cta-secondary"
                            className="group inline-flex items-center gap-3 rounded-lg glass glass-hover border border-white/15 text-white font-medium text-base px-7 py-4 transition"
                        >
                            <span className="h-7 w-7 rounded-full bg-white/10 grid place-items-center group-hover:bg-cyan-400/30 transition">
                                <Play className="h-3 w-3 text-cyan-300 fill-cyan-300 ml-0.5" />
                            </span>
                            Contact Us
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition" />
                        </a>
                    </motion.div>

                    {/* Floating ticker */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.5 }}
                        data-testid="hero-ticker"
                        className="mt-14 w-full max-w-3xl"
                    >
                        <div className="glass-strong rounded-2xl px-2 py-2 flex items-stretch overflow-hidden shadow-[0_0_60px_-20px_rgba(0,229,255,0.4)]">
                            {tickerStats.map((s, idx) => {
                                const Icon = s.icon;
                                return (
                                    <React.Fragment key={s.label}>
                                        <div className="flex-1 flex items-center justify-center gap-3 py-3 px-4">
                                            <motion.span
                                                animate={{ rotate: [0, 360] }}
                                                transition={{
                                                    duration: 14,
                                                    repeat: Infinity,
                                                    ease: "linear",
                                                }}
                                                className="shrink-0"
                                            >
                                                <Icon className="h-5 w-5 text-cyan-300" />
                                            </motion.span>
                                            <div className="text-left">
                                                <div className="font-mono text-lg md:text-xl font-bold text-white leading-none">
                                                    {s.value}
                                                </div>
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
                                                    {s.label}
                                                </div>
                                            </div>
                                        </div>
                                        {idx < tickerStats.length - 1 && (
                                            <div className="w-px bg-white/10 my-2" />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </motion.div>
                </div>
            </div>

            <motion.div
                {...fadeUp}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center text-zinc-500 z-10"
            >
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase">
                    Scroll
                </span>
                <motion.div
                    className="mt-2 h-8 w-px bg-gradient-to-b from-cyan-400 to-transparent"
                    animate={{ scaleY: [0.3, 1, 0.3] }}
                    transition={{ duration: 2.2, repeat: Infinity }}
                />
            </motion.div>
        </section>
    );
};

export default Hero;
